import { useState, useMemo, useEffect, useLayoutEffect, useRef, Fragment, type ReactNode, type CSSProperties } from 'react';
import { useLayout } from '../contexts/LayoutContext';
import { formatNumber } from '../utils/formatters';
import ToolbarActions from './ToolbarActions';
import SpeedDialFAB from './SpeedDialFAB';
import SearchSelect from './SearchSelect';
import { getStatusStyle } from '../config/statusConfig';

// ---- Types ----

/** Generic row type used throughout DataTable — uses index signature for compatibility with typed rows */
export type DataRow = { [key: string]: unknown };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface Column<R = any> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: R) => ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
  hideOnMobile?: boolean;
  /**
   * Optional column group. When set on any column, the table renders a
   * two-row <thead> with grouped header cells. Columns sharing a group
   * (same key) sit under one banner. Columns without a group span both
   * rows (rowSpan=2).
   */
  group?: {
    key: string;
    label: string;
    background?: string;
    color?: string;
  };
}

export interface FilterOption {
  value: string;
  label: string;
  matchValues?: string[];
}

export interface Filter {
  key: string;
  label: string;
  options: FilterOption[];
  matchFn?: (row: DataRow, filterValue: string) => boolean;
}

export interface SummaryCard {
  value: string | number;
  label: string;
  color?: string;
  trend?: string;
  tooltip?: string;
}

interface PrimaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Icon shown in the mobile speed-dial FAB. Defaults to '＋' for primary, '⋯' for secondary. */
  icon?: string;
}

interface OverflowAction {
  label: string;
  icon?: string;
  onClick: () => void;
}

export interface HeaderActions {
  primary?: PrimaryAction;
  secondary?: PrimaryAction;
  onSync?: (() => void) | { label: string; onClick: () => void } | Array<{ label: string; onClick: () => void }>;
  overflow?: OverflowAction[];
}

interface SortConfig {
  key: string | null;
  direction: 'asc' | 'desc';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface DataTableProps<R = any> {
  columns?: Column<R>[];
  data?: R[];
  searchable?: boolean;
  searchPlaceholder?: string;
  searchKeys?: string[];
  /** Phone fields also matched on digits only, so "+27 76 022 4476", "076 022 4476" and "27760224476" all match. */
  phoneSearchKeys?: string[];
  onRowClick?: (row: R) => void;
  emptyMessage?: string | ReactNode;
  actions?: (row: R) => ReactNode;
  filters?: Filter[];
  defaultFilterValues?: Record<string, string>;
  defaultSort?: SortConfig | null;
  headerActions?: HeaderActions | ReactNode;
  loading?: boolean;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  idKey?: string;
  resultsBarContent?: ReactNode;
  pageSize?: number;
  summaryCards?: SummaryCard[];
  description?: string;
  rowClassName?: (row: R) => string;
  renderSubRow?: (row: R) => ReactNode;
  /**
   * FIT THE LIST TO THE SCREEN INSTEAD OF LETTING IT RUN OFF THE BOTTOM. On by default.
   *
   * A wide list keeps its sideways scroll bar at the bottom of its own scroll
   * box, and that box is as tall as the list. At fifty rows the bar ends up
   * thousands of pixels down, so on Windows with a mouse, where there is no
   * sideways wheel and no trackpad gesture, the columns off to the right cannot
   * be reached without scrolling to the very end of the list first. A Mac hides
   * the problem completely, which is why it went unreported for months.
   *
   * The list is capped at the room left below it, the rows take what is left, the
   * sideways bar sits where it can be seen, and the header row freezes. Desktop
   * only; mobile is untouched, and a table with a grouped two-row header keeps
   * the cap but not the frozen header.
   *
   * It measures rather than assumes, so a page with a heading or filters above
   * the list still gets it right, and it re-measures when the page moves. A short
   * window still gets the cap, because a short window is exactly where the bar is
   * hardest to reach. Only a list that starts below the fold, meaning a second one
   * stacked under a first, and a list inside a pop-up, are left alone. Pass false
   * to turn it off outright.
   */
  fitToViewport?: boolean;
  /** Notified whenever the internal search query changes. Lets a parent mirror
   *  the query to drive things like expanding matching sub-rows. Optional. */
  onSearchChange?: (query: string) => void;
  /** Told the rows left after search and filters, in the order shown. For a page that
   *  acts on "what I am looking at", such as an export. Optional. */
  onVisibleRowsChange?: (rows: R[]) => void;
  /**
   * SAY SO WHEN THE LIST IS ONLY PART OF THE STORY.
   *
   * A screen that loads the newest few hundred rows for speed has a problem the
   * speed does not pay for: this table searches what it was given, so a search
   * for anything older comes back empty and looks like an answer. On Service
   * Logs that was 76 rows out of 6,218, and the way out was a grey line of text
   * above the table or, on Invoices, an item hidden in the overflow menu.
   *
   * Pass this and the table stops pretending: it says how much it is holding,
   * offers to fetch the rest, and, most importantly, fetches the rest by itself
   * the moment somebody searches, so nobody has to know the limit exists.
   *
   * Omit it and nothing changes, which is why every other screen is untouched.
   */
  partial?: {
    /** How many rows exist in total, not how many were loaded. */
    total: number;
    /** True while the rest is being fetched. */
    loading?: boolean;
    /** Fetch everything. Called by the button, and once automatically on search. */
    onLoadAll: () => void;
    /** What the rows are, for the wording: "of 6,218 invoices". */
    noun?: string;
  };
}

function DataTable({
  columns = [],
  data = [],
  searchable = true,
  searchPlaceholder = 'Search...',
  searchKeys = [],
  phoneSearchKeys = [],
  onRowClick,
  emptyMessage = 'No data found',
  actions,
  filters = [],
  defaultFilterValues = {},
  defaultSort = null,
  headerActions,
  loading = false,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  idKey = 'id',
  resultsBarContent,
  pageSize = 0,
  summaryCards = [],
  description = '',
  rowClassName,
  renderSubRow,
  onSearchChange,
  onVisibleRowsChange,
  partial,
  fitToViewport = true,
}: DataTableProps) {
  const { isMobile } = useLayout();

  // Is there more than we were handed. `total` can lag a row or two behind a
  // live table, so the comparison is deliberately "more than", not "not equal".
  const isPartial = !!partial && partial.total > data.length;
  const visibleColumns = useMemo(
    () => isMobile ? columns.filter(c => !c.hideOnMobile) : columns,
    [columns, isMobile]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>(defaultSort || { key: null, direction: 'asc' });
  const [filterValues, setFilterValues] = useState<Record<string, string>>(defaultFilterValues);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // ---- Fit the list to the screen (see the fitToViewport prop) ----------------
  // The height is measured rather than worked out from the viewport, because a
  // page is free to put a heading, summary cards or a filter panel above the
  // list and arithmetic would put the sideways scroll bar back under the fold on
  // every one of those. Measured from the top of the scrolling frame's CONTENT,
  // not from the top of the window, so scrolling the page does not change the
  // answer and the cap cannot oscillate.
  const cardRef = useRef<HTMLDivElement>(null);
  const [fitHeight, setFitHeight] = useState<number | null>(null);

  // The shortest a capped list is allowed to be. A small screen still gets the
  // cap, it just stops shrinking here and lets the page scroll the last few
  // pixels. That is the whole point: a small screen is exactly where the sideways
  // scroll bar is worst, so switching the fix off there hands back the bug.
  const FIT_MIN_HEIGHT = 200;
  const FIT_BOTTOM_GAP = 24;
  // A list whose top sits within this of the bottom of the frame cannot be seen
  // without scrolling to it, which means it is a second list stacked under a
  // first. That, and only that, is what gets left alone.
  const FIT_STACKED_EDGE = 80;

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!fitToViewport || isMobile || !card) {
      setFitHeight(null);
      return;
    }
    // A pop-up scrolls itself and is not sized against the page frame.
    if (card.closest('.modal, .modal-overlay')) {
      setFitHeight(null);
      return;
    }
    const frame = card.closest('.content-area') as HTMLElement | null;
    if (!frame) {
      setFitHeight(null);
      return;
    }

    const measure = () => {
      const topWithinContent =
        card.getBoundingClientRect().top - frame.getBoundingClientRect().top + frame.scrollTop;

      // A list you have to scroll down to before you can even see the top of it is
      // a second list stacked under a first. Those are left exactly as they were.
      // Everything that starts on screen is capped, however little room is left.
      if (topWithinContent > frame.clientHeight - FIT_STACKED_EDGE) {
        setFitHeight(null);
        return;
      }

      const available = frame.clientHeight - topWithinContent - FIT_BOTTOM_GAP;
      const next = Math.round(Math.max(available, FIT_MIN_HEIGHT));
      // Ignore a pixel of jitter, so a measurement that feeds back into the layout
      // it just measured cannot sit there flickering between two values.
      setFitHeight(prev => (prev !== null && Math.abs(prev - next) <= 1 ? prev : next));
    };

    measure();
    // One more pass after the browser has drawn, for anything above the list that
    // settles a frame late, such as a web font or an image.
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    // The frame keeps its own size while the page inside it grows and shrinks, so
    // watching only the frame never fires. Watch what is in it as well, so a
    // heading or a set of summary cards arriving with the data moves the cap too.
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    Array.from(frame.children).forEach(child => observer.observe(child));
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
    // Re-measured when anything that can sit above the list changes shape.
  }, [fitToViewport, isMobile, filtersExpanded, summaryCards.length, description, loading, data.length]);

  // Count active filters (non-default values)
  const activeFilterCount = Object.entries(filterValues).filter(
    ([key, value]) => value && value !== 'all' && value !== defaultFilterValues[key]
  ).length;

  // Filter and search data
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply search — split query into terms so "monique lyle" matches
    // first_name="Monique" + last_name="Lyle" across separate columns
    if (searchQuery && searchKeys.length > 0) {
      const terms = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
      // Numbers are saved in mixed formats (+27..., 27..., 0..., with spaces), so a query with
      // no letters is also matched on digits. Leading zeros are dropped and, for a full number,
      // only the last 8 digits are compared, which skips any country code.
      const isPhoneQuery = phoneSearchKeys.length > 0 && !/[a-z@]/i.test(searchQuery);
      const queryDigits = isPhoneQuery ? searchQuery.replace(/\D/g, '').replace(/^0+/, '') : '';
      const phoneNeedle = queryDigits.length >= 8 ? queryDigits.slice(-8) : queryDigits;
      const usePhone = phoneNeedle.length >= 4;
      result = result.filter(row => {
        const fieldValues = searchKeys.map(key => {
          const value = getNestedValue(row, key);
          return value ? String(value).toLowerCase() : '';
        });
        const combined = fieldValues.join(' ');
        if (terms.every(term => combined.includes(term))) return true;
        if (!usePhone) return false;
        return phoneSearchKeys.some(key => {
          const value = getNestedValue(row, key);
          return value ? String(value).replace(/\D/g, '').includes(phoneNeedle) : false;
        });
      });
    }

    // Apply filters
    Object.entries(filterValues).forEach(([filterKey, filterValue]) => {
      if (filterValue && filterValue !== 'all') {
        // Find the filter config to check for matchValues or custom matchFn
        const filterConfig = filters.find(f => f.key === filterKey);
        const optionConfig = filterConfig?.options?.find(o => o.value === filterValue);
        const matchValues = optionConfig?.matchValues;

        result = result.filter(row => {
          // If custom matchFn is defined, use it
          if (filterConfig?.matchFn) {
            return filterConfig.matchFn(row, filterValue);
          }
          const rowValue = getNestedValue(row, filterKey);
          // If matchValues is defined, check if rowValue is in that array
          if (matchValues) {
            return matchValues.includes(rowValue as string);
          }
          // Otherwise do simple equality
          return rowValue === filterValue;
        });
      }
    });

    return result;
  }, [data, searchQuery, searchKeys, phoneSearchKeys, filterValues, filters]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = getNestedValue(a, sortConfig.key!);
      const bVal = getNestedValue(b, sortConfig.key!);

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else if (aVal instanceof Date && bVal instanceof Date) {
        comparison = aVal.getTime() - bVal.getTime();
      } else {
        // Use numeric-aware locale compare for natural sorting (e.g., "Room 2" before "Room 10")
        comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      }

      return sortConfig.direction === 'desc' ? -comparison : comparison;
    });
  }, [filteredData, sortConfig]);

  useEffect(() => {
    onVisibleRowsChange?.(sortedData);
  }, [sortedData, onVisibleRowsChange]);

  // Paginate data
  const totalPages = pageSize > 0 ? Math.ceil(sortedData.length / pageSize) : 1;
  const paginatedData = useMemo(() => {
    if (pageSize <= 0) return sortedData;
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  // Reset to page 1 when filters/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterValues]);

  // Mirror the search query out to an interested parent (optional).
  useEffect(() => {
    onSearchChange?.(searchQuery);
  }, [searchQuery, onSearchChange]);

  // SEARCHING FETCHES THE REST, WITHOUT BEING ASKED.
  //
  // This is the whole point of the partial contract. Searching a list that
  // holds the newest 50 rows silently answers "nothing found" for anything
  // older, and an empty result reads as a fact rather than as a limitation.
  // The moment somebody types, the rest is fetched, so the limit only ever
  // affects how fast the page opened.
  //
  // Once per table. The ref stops a re-render or a second keystroke asking
  // again while the first fetch is still in flight.
  const askedForAll = useRef(false);
  useEffect(() => {
    if (!searchQuery || !isPartial || askedForAll.current) return;
    askedForAll.current = true;
    partial?.onLoadAll();
  }, [searchQuery, isPartial, partial]);

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
  };

  // Selection handlers (works on current page only)
  const handleSelectAll = () => {
    if (!onSelectionChange) return;

    const pageIds = paginatedData.map(row => String(row[idKey] ?? ''));
    const allSelected = pageIds.every(id => selectedIds.has(id));

    if (allSelected) {
      // Deselect all on current page
      const newSet = new Set(selectedIds);
      pageIds.forEach(id => newSet.delete(id));
      onSelectionChange(newSet);
    } else {
      // Select all on current page
      const newSet = new Set(selectedIds);
      pageIds.forEach(id => newSet.add(id));
      onSelectionChange(newSet);
    }
  };

  const handleSelectRow = (row: DataRow) => {
    if (!onSelectionChange) return;

    const id = String(row[idKey] ?? '');
    const newSet = new Set(selectedIds);

    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }

    onSelectionChange(newSet);
  };

  const isAllSelected = paginatedData.length > 0 && paginatedData.every(row => selectedIds.has(String(row[idKey] ?? '')));

  if (loading) {
    return (
      <div className="page-loader">
        <div className="spinner" />
      </div>
    );
  }

  // Extract primary + secondary for the mobile FAB. The toolbar row is hidden
  // on mobile, so any actions there must be re-surfaced. Single action → simple
  // FAB; primary + secondary → SpeedDialFAB so the secondary stays reachable.
  const primaryAction = (headerActions && typeof headerActions === 'object' && 'primary' in (headerActions as HeaderActions))
    ? (headerActions as HeaderActions).primary
    : undefined;
  const secondaryAction = (headerActions && typeof headerActions === 'object' && 'secondary' in (headerActions as HeaderActions))
    ? (headerActions as HeaderActions).secondary
    : undefined;
  const fabActions: Array<{ label: string; icon: string; onClick: () => void }> = [];
  if (primaryAction && primaryAction.label && !primaryAction.disabled) {
    fabActions.push({ label: primaryAction.label, icon: primaryAction.icon || '＋', onClick: primaryAction.onClick });
  }
  if (secondaryAction && secondaryAction.label && !secondaryAction.disabled) {
    fabActions.push({ label: secondaryAction.label, icon: secondaryAction.icon || '⋯', onClick: secondaryAction.onClick });
  }

  return (
    <>
    <div
      ref={cardRef}
      className={`card${fitHeight !== null ? ' card--fit-viewport' : ''}${
        fitHeight !== null && visibleColumns.some(c => c.group) ? ' card--fit-viewport-grouped' : ''
      }`}
      /* A measured number, so it can only come from here. The rule that uses it
         lives in global.css; this hands it the one value CSS cannot work out. */
      style={fitHeight !== null ? ({ '--fit-max-height': `${fitHeight}px` } as CSSProperties) : undefined}
    >
      {/* Summary Cards (Reports) */}
      {summaryCards.length > 0 && (
        <SummaryCardsHeader cards={summaryCards} />
      )}

      {/* Page Description */}
      {description && (
        <div className="datatable-description">
          {description}
        </div>
      )}

      {/* Toolbar */}
      <div className="list-toolbar">
        <div className="list-toolbar-left">
          {/* Select All checkbox */}
          {selectable && paginatedData.length > 0 && (
            <label className="list-select-all">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleSelectAll}
              />
              Select All
            </label>
          )}

          {/* Search */}
          {searchable && (
            <div className="list-search">
              <span className="list-search-icon">🔍</span>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="list-search-clear"
                  onClick={() => setSearchQuery('')}
                >
                  ✕
                </button>
              )}
            </div>
          )}

          {/* Mobile: Filter toggle button */}
          {filters.length > 0 && (
            <button
              className="list-filter-toggle"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
            >
              <span>⚙</span>
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="list-filter-badge">{activeFilterCount}</span>
              )}
              <span className="list-filter-arrow">{filtersExpanded ? '▲' : '▼'}</span>
            </button>
          )}

          {/* Desktop: Inline filters */}
          <div className="list-filters-inline">
            {filters.map(filter => (
              <SearchSelect
                key={filter.key}
                width="inline"
                options={[
                  { value: 'all', label: `${filter.label}: All` },
                  ...filter.options.map(opt => ({ value: opt.value, label: opt.label })),
                ]}
                value={filterValues[filter.key] || 'all'}
                onChange={(v) => handleFilterChange(filter.key, v)}
              />
            ))}
          </div>
        </div>

        {/* Header Actions */}
        {headerActions && (
          <div className="list-toolbar-right">
            {/* New format: object with primary/secondary/onSync/overflow keys (check key existence, not truthiness) */}
            {(typeof headerActions === 'object' && ('primary' in (headerActions as HeaderActions) || 'secondary' in (headerActions as HeaderActions) || 'onSync' in (headerActions as HeaderActions) || 'overflow' in (headerActions as HeaderActions))) ? (
              <ToolbarActions
                primary={(headerActions as HeaderActions).primary}
                secondary={(headerActions as HeaderActions).secondary}
                onSync={(headerActions as HeaderActions).onSync}
                overflow={(headerActions as HeaderActions).overflow}
              />
            ) : (
              /* Legacy format: ReactNode */
              headerActions as ReactNode
            )}
          </div>
        )}
      </div>

      {/* Mobile: Expandable filters */}
      {filters.length > 0 && (
        <div className={`list-filters-mobile ${filtersExpanded ? 'expanded' : ''}`}>
          {filters.map(filter => (
            <SearchSelect
              key={filter.key}
              width="inline"
              options={[
                { value: 'all', label: `${filter.label}: All` },
                ...filter.options.map(opt => ({ value: opt.value, label: opt.label })),
              ]}
              value={filterValues[filter.key] || 'all'}
              onChange={(v) => handleFilterChange(filter.key, v)}
            />
          ))}
        </div>
      )}

      {/* Results count. Where the data is partial this is also where the table
          admits it and offers the rest, because it is the one line on the
          screen that already claims to say how much you are looking at. */}
      <div className={`results-bar${isPartial ? ' flex-between gap-sm' : ''}`}>
        <span>
          {resultsBarContent || (
            <>
              {/* "76 of 76 items" beside "Newest 76 of 6,218" is the same number
                  twice and reads as a contradiction. When the list is partial
                  the right-hand side carries the counts, so this side only
                  speaks when it has something else to say. */}
              {isPartial
                ? (searchQuery ? `${sortedData.length} matching "${searchQuery}"` : '')
                : (
                  <>
                    {sortedData.length} of {data.length} items
                    {searchQuery && ` matching "${searchQuery}"`}
                  </>
                )}
              {selectable && selectedIds.size > 0 && (
                <span className="ml-md fw-600 text-primary">
                  | {selectedIds.size} selected
                </span>
              )}
            </>
          )}
        </span>
        {isPartial && (
          <span className="flex gap-2">
            <span>
              {partial!.loading
                ? `Loading all ${formatNumber(partial!.total)}...`
                : `Newest ${formatNumber(data.length)} of ${formatNumber(partial!.total)}${partial!.noun ? ` ${partial!.noun}` : ''}`}
            </span>
            {!partial!.loading && (
              <button type="button" className="btn-link" onClick={() => partial!.onLoadAll()}>
                Load all
              </button>
            )}
          </span>
        )}
      </div>

      {/* Table */}
      {paginatedData.length === 0 ? (
        <div className="empty-state">
          {/* A SEARCH THAT FOUND NOTHING IN A PARTIAL LIST HAS NOT ANSWERED THE
              QUESTION. The rest is already being fetched by the effect above,
              so this says what is happening rather than "no results", which
              would be a lie somebody acts on. */}
          {isPartial && searchQuery ? (
            <>
              <p className="empty-state-message">
                {partial!.loading
                  ? `Nothing in the newest ${formatNumber(data.length)}. Searching all ${formatNumber(partial!.total)}...`
                  : `Nothing matching "${searchQuery}" in the newest ${formatNumber(data.length)}.`}
              </p>
              {!partial!.loading && (
                <p className="empty-state-hint">
                  There are {formatNumber(partial!.total)} in total.{' '}
                  <button type="button" className="btn-link" onClick={() => partial!.onLoadAll()}>
                    Search all of them
                  </button>
                </p>
              )}
            </>
          ) : typeof emptyMessage === 'string' ? (
            <p className="empty-state-message">{emptyMessage}</p>
          ) : (
            emptyMessage
          )}
        </div>
      ) : (
        <div className="mobile-table-scroll">
          <table className="data-table">
            <thead>
              {(() => {
                const hasGroups = visibleColumns.some(c => c.group);
                if (!hasGroups) {
                  return (
                    <tr>
                      {selectable && (
                        <th className="table-col-checkbox">
                          <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
                        </th>
                      )}
                      {visibleColumns.map(col => (
                        <th
                          key={col.key}
                          className={`${col.sortable ? 'sortable' : ''} ${col.align ? `text-${col.align}` : 'text-left'}`}
                          style={{ width: col.width }}
                          onClick={() => col.sortable && handleSort(col.key)}
                        >
                          <span>{col.label}</span>
                          {col.sortable && (
                            <span className="sort-icon">
                              {sortConfig.key === col.key
                                ? sortConfig.direction === 'asc' ? ' ↑' : ' ↓'
                                : ' ↕'}
                            </span>
                          )}
                        </th>
                      ))}
                      {actions && <th className="table-col-actions">Actions</th>}
                    </tr>
                  );
                }
                // Two-row grouped header. Columns with no `group` span both rows
                // (rowSpan=2). Columns with a `group` get a top-row banner with
                // colSpan = number of consecutive columns sharing that group.
                type Span = { col: Column; span: number };
                const banners: Array<Span | { col: null; group: Column['group']; span: number }> = [];
                let i = 0;
                while (i < visibleColumns.length) {
                  const col = visibleColumns[i]!;
                  if (!col.group) {
                    banners.push({ col, span: 1 });
                    i++;
                  } else {
                    const groupKey = col.group.key;
                    let j = i;
                    while (j < visibleColumns.length && visibleColumns[j]!.group?.key === groupKey) j++;
                    banners.push({ col: null, group: col.group, span: j - i });
                    i = j;
                  }
                }
                return (
                  <>
                    <tr>
                      {selectable && (
                        <th className="table-col-checkbox" rowSpan={2}>
                          <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
                        </th>
                      )}
                      {banners.map((b, idx) => {
                        if (b.col && !('group' in b)) {
                          // Non-grouped column — span both header rows
                          const col = b.col;
                          return (
                            <th
                              key={`top-${col.key}`}
                              rowSpan={2}
                              className={`${col.sortable ? 'sortable' : ''} ${col.align ? `text-${col.align}` : 'text-left'}`}
                              style={{ width: col.width }}
                              onClick={() => col.sortable && handleSort(col.key)}
                            >
                              <span>{col.label}</span>
                              {col.sortable && (
                                <span className="sort-icon">
                                  {sortConfig.key === col.key
                                    ? sortConfig.direction === 'asc' ? ' ↑' : ' ↓'
                                    : ' ↕'}
                                </span>
                              )}
                            </th>
                          );
                        }
                        const grp = (b as { group: Column['group']; span: number }).group!;
                        return (
                          <th
                            key={`group-${grp.key}-${idx}`}
                            colSpan={b.span}
                            className="text-center"
                            style={{ background: grp.background, color: grp.color, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: 'var(--text-xs)' }}
                          >
                            {grp.label}
                          </th>
                        );
                      })}
                      {actions && <th className="table-col-actions" rowSpan={2}>Actions</th>}
                    </tr>
                    <tr>
                      {visibleColumns.filter(c => c.group).map(col => (
                        <th
                          key={`sub-${col.key}`}
                          className={`${col.sortable ? 'sortable' : ''} ${col.align ? `text-${col.align}` : 'text-left'}`}
                          style={{ width: col.width, background: col.group?.background, fontWeight: 600, fontSize: 'var(--text-sm)' }}
                          onClick={() => col.sortable && handleSort(col.key)}
                        >
                          <span>{col.label}</span>
                          {col.sortable && (
                            <span className="sort-icon">
                              {sortConfig.key === col.key
                                ? sortConfig.direction === 'asc' ? ' ↑' : ' ↓'
                                : ' ↕'}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </>
                );
              })()}
            </thead>
            <tbody>
              {paginatedData.map((row, idx) => {
                const rowKey = String(row[idKey] ?? idx);
                const subContent = renderSubRow ? renderSubRow(row) : null;
                return (
                  <Fragment key={rowKey}>
                    <tr
                      className={`${rowClassName ? rowClassName(row) : ''} ${onRowClick ? 'table-row-clickable' : ''}`}
                      onClick={() => onRowClick && onRowClick(row)}
                    >
                      {/* Checkbox cell */}
                      {selectable && (
                        <td className="text-center" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(String(row[idKey] ?? ''))}
                            onChange={() => handleSelectRow(row)}
                          />
                        </td>
                      )}

                      {/* Data cells */}
                      {visibleColumns.map(col => (
                        <td
                          key={col.key}
                          className={col.align ? `text-${col.align}` : 'text-left'}
                          style={col.group?.background ? { background: col.group.background } : undefined}
                        >
                          {col.render
                            ? col.render(row)
                            : (getNestedValue(row, col.key) as ReactNode) ?? '-'}
                        </td>
                      ))}

                      {/* Actions cell */}
                      {actions && (
                        <td className="text-center" onClick={e => e.stopPropagation()}>
                          <div className="table-actions">
                            {actions(row)}
                          </div>
                        </td>
                      )}
                    </tr>
                    {subContent && (
                      <tr className="table-sub-row">
                        <td colSpan={visibleColumns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)} style={{ padding: 0 }}>
                          {subContent}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pageSize > 0 && totalPages > 1 && (
        <div className="table-pagination">
          <button
            className="table-pagination-btn"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            «
          </button>
          <button
            className="table-pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          <span className="table-pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="table-pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
          <button
            className="table-pagination-btn"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            »
          </button>
        </div>
      )}

    </div>

      {/* Mobile FAB. Single action → simple "+" button. Primary + secondary
          → SpeedDialFAB so the secondary stays reachable on mobile (the
          .list-toolbar-right row is hidden at <=768px). */}
      {fabActions.length === 1 && primaryAction && (
        <button
          className="fab"
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
          title={primaryAction.label}
        >
          +
        </button>
      )}
      {fabActions.length >= 2 && (
        <SpeedDialFAB actions={fabActions} />
      )}
    </>
  );
}

// Helper to get nested object values like "user.name"
function getNestedValue(obj: DataRow, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

// Summary Cards Header - 4-card KPI layout for reports
function SummaryCardsHeader({ cards }: { cards: SummaryCard[] }) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Limit to 4 cards
  const displayCards = cards.slice(0, 4);

  return (
    <div className="summary-cards">
      {displayCards.map((card, index) => (
        <div
          key={index}
          className={`summary-card summary-card--${card.color || 'primary'} ${hoveredIndex === index ? 'summary-card--hovered' : ''}`}
          onMouseEnter={() => card.tooltip && setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <div className="summary-card__value">{card.value}</div>
          {card.trend && (
            <div className={`summary-card__trend ${
              card.trend.startsWith('+') ? 'summary-card__trend--up' :
              card.trend.startsWith('-') ? 'summary-card__trend--down' : ''
            }`}>
              {card.trend}
            </div>
          )}
          <div className="summary-card__label">{card.label}</div>
          {card.tooltip && hoveredIndex === index && (
            <div className="summary-card__tooltip">
              {card.tooltip}
              <div className="summary-card__tooltip-arrow" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Status Badge Component - reusable across all list views
// Supports two APIs:
// 1. <StatusBadge status="active" config={statusConfig} />
// 2. <StatusBadge bg="#d4edda" color="var(--color-success-text)">Active</StatusBadge>
interface StatusBadgeConfig {
  [key: string]: { label: string; bg: string; color: string; icon?: string };
}

interface StatusBadgeProps {
  status?: string;
  config?: StatusBadgeConfig;
  bg?: string;
  color?: string;
  children?: ReactNode;
}

export function StatusBadge({ status, config, bg, color, children }: StatusBadgeProps) {
  // If using direct bg/color props (simpler API)
  if (bg !== undefined || children !== undefined) {
    return (
      <span className="status-badge" style={{
        background: bg || 'var(--color-border)',
        color: color || 'var(--color-text-light)',
      }}>
        {children}
      </span>
    );
  }

  // If using status + config (original API). When the caller's config has no
  // entry for this status — or no config was passed at all — fall back to the
  // canonical shared palette so the same status looks identical everywhere,
  // instead of a bare grey pill.
  const statusConfig = config?.[status || ''] || getStatusStyle(status);
  return (
    <span className="status-badge" style={{
      background: statusConfig.bg,
      color: statusConfig.color,
    }}>
      {statusConfig.icon && <span>{statusConfig.icon}</span>}
      {statusConfig.label}
    </span>
  );
}

export default DataTable;
